import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Table,
  Button,
  Input,
  Modal,
  Form,
  Select,
  InputNumber,
  DatePicker,
  Tag,
  Space,
  Tooltip,
  Popconfirm,
  Typography,
  message,
  Row,
  Col,
  Card,
  Divider,
  Descriptions,
  Empty,
  Alert,
  Radio
} from 'antd';
import {
  PlusOutlined,
  SearchOutlined,
  ReloadOutlined,
  ExclamationCircleOutlined,
  EyeOutlined,
  DeleteOutlined,
  CheckOutlined,
  FileSearchOutlined,
  SafetyCertificateOutlined,
  EditOutlined,
  PrinterOutlined,
  FileExcelOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { useReactToPrint } from 'react-to-print';
import { utils, writeFile } from 'xlsx';
import { facturasService } from '../services/api';
import { useAuth } from '../context/AuthContext';
import FacturaPrint from '../components/print/FacturaPrint';

const { Title, Text } = Typography;
const { TextArea } = Input;
const { Option } = Select;

const generarKey = () =>
  (typeof crypto !== 'undefined' && crypto.randomUUID) ? crypto.randomUUID() : `li-${Date.now()}-${Math.random()}`;

const STATUS_CONFIG = {
  creada:     { color: 'processing', label: 'Pend. Revisión' },
  revisada:   { color: 'blue',       label: 'Pend. Confirmación' },
  confirmada: { color: 'success',    label: 'Confirmada' }
};

const formatCurrency = (amount) => {
  if (amount == null || amount === '') return '—';
  return new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(amount);
};

const nuevaLinea = () => ({
  key: generarKey(),
  descripcion: '',
  cantidad: 1,
  costo_unitario: 0,
  iva_porcentaje: 16
});

const calcularLinea = (li) => {
  const subtotal = (parseFloat(li.cantidad) || 0) * (parseFloat(li.costo_unitario) || 0);
  const ivaMonto = subtotal * ((parseFloat(li.iva_porcentaje) || 0) / 100);
  return { subtotal, ivaMonto, total: subtotal + ivaMonto };
};

function Facturas() {
  const { user } = useAuth();
  const rol = user?.rol;
  const canCrear = ['root', 'admin', 'gerencia', 'rh'].includes(rol);
  const canRevisar = ['root', 'admin', 'gerencia'].includes(rol);
  const canConfirmar = ['root', 'gerencia'].includes(rol);
  const canDelete = rol === 'root';

  const [facturas, setFacturas] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [fechaRange, setFechaRange] = useState(null);

  const [modalOpen, setModalOpen] = useState(false);
  const [editingFactura, setEditingFactura] = useState(null);
  const [saving, setSaving] = useState(false);
  const [lineItems, setLineItems] = useState([]);
  const [form] = Form.useForm();

  const [detailOpen, setDetailOpen] = useState(false);
  const [detailFactura, setDetailFactura] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [revisionMode, setRevisionMode] = useState(false);
  const [revisionValues, setRevisionValues] = useState({});
  const [confirmMode, setConfirmMode] = useState(false);
  const [confirmDecisions, setConfirmDecisions] = useState({});

  const [printData, setPrintData] = useState(null);
  const printRef = useRef(null);

  const triggerPrint = useReactToPrint({
    content: () => printRef.current,
    documentTitle: `Factura ${printData?.numero_factura || ''}`,
    pageStyle: `@page { size: A4; margin: 1.2cm; } body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }`,
    onAfterPrint: () => setPrintData(null)
  });

  useEffect(() => {
    if (printData === null) return;
    triggerPrint();
  }, [printData]); // eslint-disable-line

  const fetchFacturas = useCallback(async (overrides = {}) => {
    setLoading(true);
    try {
      const s = 'search' in overrides ? overrides.search : search;
      const st = 'status' in overrides ? overrides.status : statusFilter;
      const range = 'fechaRange' in overrides ? overrides.fechaRange : fechaRange;

      const params = {};
      if (s) params.search = s;
      if (st) params.status = st;
      if (range && range[0] && range[1]) {
        params.fecha_desde = range[0].format('YYYY-MM-DD');
        params.fecha_hasta = range[1].format('YYYY-MM-DD');
      }

      const res = await facturasService.getAll(params);
      setFacturas(res.data.data || []);
    } catch (error) {
      message.error(error.displayMessage || 'Error al cargar facturas');
    } finally {
      setLoading(false);
    }
  }, [search, statusFilter, fechaRange]);

  useEffect(() => {
    fetchFacturas();
  }, [fetchFacturas]);

  const handleSearch = (value) => {
    setSearch(value);
    fetchFacturas({ search: value });
  };

  const handleStatusFilter = (value) => {
    setStatusFilter(value || '');
    fetchFacturas({ status: value || '' });
  };

  const handleFechaRangeFilter = (range) => {
    setFechaRange(range);
    fetchFacturas({ fechaRange: range });
  };

  // ── Crear / Editar ─────────────────────────────────────────────────────
  const openCreateModal = () => {
    setEditingFactura(null);
    form.resetFields();
    form.setFieldsValue({ fecha_factura: dayjs() });
    setLineItems([nuevaLinea()]);
    setModalOpen(true);
  };

  const openEditModal = (record) => {
    setEditingFactura(record);
    form.setFieldsValue({
      numero_factura: record.numero_factura,
      proveedor_nombre: record.proveedor_nombre,
      rfc_proveedor: record.rfc_proveedor,
      fecha_factura: dayjs(record.fecha_factura),
      concepto: record.concepto,
      notas: record.notas
    });
    setLineItems(
      (record.detalles || []).map((d) => ({
        key: d.id,
        descripcion: d.descripcion_original,
        cantidad: parseFloat(d.cantidad_original),
        costo_unitario: parseFloat(d.costo_unitario_original),
        iva_porcentaje: parseFloat(d.iva_porcentaje_original)
      }))
    );
    setModalOpen(true);
  };

  const addLineItem = () => setLineItems([...lineItems, nuevaLinea()]);
  const removeLineItem = (key) => setLineItems(lineItems.filter((li) => li.key !== key));
  const updateLineItem = (key, field, value) =>
    setLineItems((items) => items.map((li) => (li.key === key ? { ...li, [field]: value } : li)));

  const totalesForm = lineItems.reduce(
    (acc, li) => {
      const { subtotal, ivaMonto, total } = calcularLinea(li);
      return { subtotal: acc.subtotal + subtotal, iva: acc.iva + ivaMonto, total: acc.total + total };
    },
    { subtotal: 0, iva: 0, total: 0 }
  );

  const handleSaveFactura = async () => {
    try {
      const values = await form.validateFields();
      if (lineItems.length === 0) {
        message.warning('Agrega al menos un producto a la factura');
        return;
      }
      if (lineItems.some((li) => !li.descripcion?.trim())) {
        message.warning('Todos los productos deben tener una descripción');
        return;
      }

      setSaving(true);
      const payload = {
        numero_factura: values.numero_factura,
        proveedor_nombre: values.proveedor_nombre,
        rfc_proveedor: values.rfc_proveedor,
        fecha_factura: values.fecha_factura.format('YYYY-MM-DD'),
        concepto: values.concepto,
        notas: values.notas,
        detalles: lineItems.map((li) => ({
          descripcion: li.descripcion,
          cantidad: li.cantidad,
          costo_unitario: li.costo_unitario,
          iva_porcentaje: li.iva_porcentaje
        }))
      };

      if (editingFactura) {
        await facturasService.update(editingFactura.id, payload);
        message.success('Factura actualizada exitosamente');
      } else {
        await facturasService.create(payload);
        message.success('Factura creada exitosamente');
      }

      setModalOpen(false);
      fetchFacturas();
    } catch (error) {
      if (error.errorFields) return;
      message.error(error.displayMessage || 'Error al guardar la factura');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (record) => {
    try {
      await facturasService.delete(record.id);
      message.success(`Factura "${record.numero_factura}" eliminada exitosamente`);
      fetchFacturas();
    } catch (error) {
      message.error(error.displayMessage || 'Error al eliminar la factura');
    }
  };

  // ── Impresión ───────────────────────────────────────────────────────────
  const handlePrint = async (record) => {
    try {
      const res = await facturasService.getById(record.id);
      setPrintData(res.data.data);
    } catch {
      message.error('Error al preparar la impresión');
    }
  };

  // ── Excel: exportación masiva (listado filtrado actual) ──────────────────
  const handleExportExcel = () => {
    if (facturas.length === 0) {
      message.warning('No hay facturas para exportar');
      return;
    }

    const sheetFacturas = facturas.map((f) => ({
      'N° Factura': f.numero_factura,
      'Proveedor': f.proveedor_nombre,
      'RFC': f.rfc_proveedor || '',
      'Fecha Factura': f.fecha_factura ? dayjs(f.fecha_factura).format('DD/MM/YYYY') : '',
      'Concepto': f.concepto || '',
      'Estado': STATUS_CONFIG[f.status]?.label || f.status,
      'Subtotal': parseFloat(f.subtotal) || 0,
      'IVA': parseFloat(f.iva_total) || 0,
      'Total': parseFloat(f.total) || 0,
      'Creada por': f.creador?.nombre || '',
      'Revisada por': f.revisor?.nombre || '',
      'Confirmada por': f.confirmador?.nombre || ''
    }));

    const sheetDetalle = [];
    facturas.forEach((f) => {
      (f.detalles || []).forEach((d) => {
        sheetDetalle.push({
          'N° Factura': f.numero_factura,
          'Descripción': d.descripcion_efectiva,
          'Cantidad': d.cantidad_efectiva,
          'Costo Unitario': d.costo_unitario_efectivo,
          'IVA %': d.iva_porcentaje_efectivo,
          'Subtotal': d.subtotal_efectivo,
          'IVA': d.iva_monto_efectivo,
          'Total': d.total_efectivo,
          '¿Corregido?': d.fue_corregido ? (d.usar_original ? 'Sí (revertido a original)' : 'Sí') : 'No',
          'Descripción Original': d.descripcion_original,
          'Cantidad Original': parseFloat(d.cantidad_original),
          'Costo Original': parseFloat(d.costo_unitario_original),
          'IVA % Original': parseFloat(d.iva_porcentaje_original)
        });
      });
    });

    const wb = utils.book_new();
    utils.book_append_sheet(wb, utils.json_to_sheet(sheetFacturas), 'Facturas');
    utils.book_append_sheet(wb, utils.json_to_sheet(sheetDetalle), 'Detalle Productos');

    writeFile(wb, `Facturas_${dayjs().format('YYYYMMDD_HHmm')}.xlsx`);
  };

  // ── Excel: exportación individual (todo en una hoja, igual que el impreso) ──
  const handleExportFacturaExcel = async (record) => {
    try {
      const res = await facturasService.getById(record.id);
      const factura = res.data.data;
      const detalles = factura.detalles || [];

      const filas = [
        ['FACTURA', factura.numero_factura],
        ['Estado', STATUS_CONFIG[factura.status]?.label || factura.status],
        [],
        ['PROVEEDOR / EMISOR'],
        ['Nombre', factura.proveedor_nombre],
        ['RFC', factura.rfc_proveedor || ''],
        ['Fecha de Factura', factura.fecha_factura ? dayjs(factura.fecha_factura).format('DD/MM/YYYY') : ''],
        ['Concepto', factura.concepto || ''],
        [],
        ['SEGUIMIENTO'],
        ['Creada por', factura.creador?.nombre || ''],
        ['Revisada por', factura.revisor?.nombre || ''],
        ['Fecha de Revisión', factura.fecha_revision ? dayjs(factura.fecha_revision).format('DD/MM/YYYY HH:mm') : ''],
        ['Confirmada por', factura.confirmador?.nombre || ''],
        ['Fecha de Confirmación', factura.fecha_confirmacion ? dayjs(factura.fecha_confirmacion).format('DD/MM/YYYY HH:mm') : ''],
        ['Notas', factura.notas || ''],
        [],
        ['DETALLE DE PRODUCTOS (valores finales)'],
        ['Descripción', 'Cantidad', 'Costo Unitario', 'IVA %', 'Subtotal', 'IVA', 'Total', '¿Corregido?'],
        ...detalles.map((d) => [
          d.descripcion_efectiva,
          d.cantidad_efectiva,
          d.costo_unitario_efectivo,
          d.iva_porcentaje_efectivo,
          d.subtotal_efectivo,
          d.iva_monto_efectivo,
          d.total_efectivo,
          d.fue_corregido ? (d.usar_original ? 'Sí (revertido a original)' : 'Sí') : 'No'
        ]),
        [],
        ['DATOS ORIGINALES (como se ingresaron en la creación)'],
        ['Descripción', 'Cantidad', 'Costo Unitario', 'IVA %'],
        ...detalles.map((d) => [
          d.descripcion_original,
          parseFloat(d.cantidad_original),
          parseFloat(d.costo_unitario_original),
          parseFloat(d.iva_porcentaje_original)
        ]),
        [],
        ['', '', '', '', '', 'Subtotal', parseFloat(factura.subtotal) || 0],
        ['', '', '', '', '', 'IVA', parseFloat(factura.iva_total) || 0],
        ['', '', '', '', '', 'Total', parseFloat(factura.total) || 0]
      ];

      const ws = utils.aoa_to_sheet(filas);
      ws['!cols'] = [
        { wch: 30 }, { wch: 12 }, { wch: 15 }, { wch: 10 }, { wch: 13 }, { wch: 13 }, { wch: 13 }, { wch: 20 }
      ];

      const wb = utils.book_new();
      utils.book_append_sheet(wb, ws, 'Factura');

      writeFile(wb, `Factura_${factura.numero_factura}.xlsx`);
    } catch {
      message.error('Error al generar el Excel de la factura');
    }
  };

  // ── Detalle / Revisión / Confirmación ─────────────────────────────────
  const openDetail = async (record) => {
    setDetailOpen(true);
    setDetailLoading(true);
    setRevisionMode(false);
    setConfirmMode(false);
    try {
      const res = await facturasService.getById(record.id);
      setDetailFactura(res.data.data);
    } catch (error) {
      message.error(error.displayMessage || 'Error al cargar la factura');
      setDetailOpen(false);
    } finally {
      setDetailLoading(false);
    }
  };

  const refreshDetail = async (id) => {
    const res = await facturasService.getById(id);
    setDetailFactura(res.data.data);
  };

  const startRevision = () => {
    const initial = {};
    (detailFactura.detalles || []).forEach((d) => {
      initial[d.id] = {
        descripcion: d.descripcion_original,
        cantidad: parseFloat(d.cantidad_original),
        costo_unitario: parseFloat(d.costo_unitario_original),
        iva_porcentaje: parseFloat(d.iva_porcentaje_original)
      };
    });
    setRevisionValues(initial);
    setRevisionMode(true);
  };

  const updateRevisionValue = (id, field, value) =>
    setRevisionValues((prev) => ({ ...prev, [id]: { ...prev[id], [field]: value } }));

  const handleGuardarRevision = async () => {
    setActionLoading(true);
    try {
      const correcciones = [];
      (detailFactura.detalles || []).forEach((d) => {
        const v = revisionValues[d.id];
        if (!v) return;
        const cambioDescripcion = v.descripcion?.trim() !== d.descripcion_original;
        const cambioCantidad = parseFloat(v.cantidad) !== parseFloat(d.cantidad_original);
        const cambioCosto = parseFloat(v.costo_unitario) !== parseFloat(d.costo_unitario_original);
        const cambioIva = parseFloat(v.iva_porcentaje) !== parseFloat(d.iva_porcentaje_original);

        if (cambioDescripcion || cambioCantidad || cambioCosto || cambioIva) {
          correcciones.push({
            id: d.id,
            descripcion: v.descripcion,
            cantidad: v.cantidad,
            costo_unitario: v.costo_unitario,
            iva_porcentaje: v.iva_porcentaje
          });
        }
      });

      await facturasService.revisar(detailFactura.id, correcciones);
      message.success('Factura marcada como revisada');
      setRevisionMode(false);
      await refreshDetail(detailFactura.id);
      fetchFacturas();
    } catch (error) {
      message.error(error.displayMessage || 'Error al guardar la revisión');
    } finally {
      setActionLoading(false);
    }
  };

  const lineasConCorreccion = (detailFactura?.detalles || []).filter((d) => d.fue_corregido);

  const startConfirm = () => {
    const initial = {};
    lineasConCorreccion.forEach((d) => {
      initial[d.id] = d.usar_original ? 'original' : 'corregido';
    });
    setConfirmDecisions(initial);
    setConfirmMode(true);
  };

  const handleConfirmarDirecto = async () => {
    setActionLoading(true);
    try {
      await facturasService.confirmar(detailFactura.id, []);
      message.success('Factura confirmada');
      await refreshDetail(detailFactura.id);
      fetchFacturas();
    } catch (error) {
      message.error(error.displayMessage || 'Error al confirmar la factura');
    } finally {
      setActionLoading(false);
    }
  };

  const handleConfirmarConDecisiones = async () => {
    setActionLoading(true);
    try {
      const decisiones = lineasConCorreccion.map((d) => ({
        id: d.id,
        usar_original: confirmDecisions[d.id] === 'original'
      }));
      await facturasService.confirmar(detailFactura.id, decisiones);
      message.success('Factura confirmada');
      setConfirmMode(false);
      await refreshDetail(detailFactura.id);
      fetchFacturas();
    } catch (error) {
      message.error(error.displayMessage || 'Error al confirmar la factura');
    } finally {
      setActionLoading(false);
    }
  };

  // ── Columnas tabla principal ───────────────────────────────────────────
  const columns = [
    {
      title: 'N° Factura',
      dataIndex: 'numero_factura',
      key: 'numero_factura',
      width: 140,
      render: (val) => <Text code strong>{val}</Text>
    },
    { title: 'Proveedor', dataIndex: 'proveedor_nombre', key: 'proveedor_nombre' },
    {
      title: 'Fecha Factura',
      dataIndex: 'fecha_factura',
      key: 'fecha_factura',
      width: 120,
      render: (val) => (val ? dayjs(val).format('DD/MM/YYYY') : '—')
    },
    {
      title: 'Total',
      dataIndex: 'total',
      key: 'total',
      width: 120,
      align: 'right',
      render: (val) => formatCurrency(val)
    },
    {
      title: 'Estado',
      dataIndex: 'status',
      key: 'status',
      width: 160,
      filters: Object.entries(STATUS_CONFIG).map(([value, cfg]) => ({ text: cfg.label, value })),
      onFilter: (value, record) => record.status === value,
      render: (status) => <Tag color={STATUS_CONFIG[status]?.color}>{STATUS_CONFIG[status]?.label || status}</Tag>
    },
    {
      title: 'Creada por',
      key: 'creador',
      width: 130,
      render: (_, record) => record.creador?.nombre || '—'
    },
    {
      title: 'Acciones',
      key: 'acciones',
      width: 190,
      fixed: 'right',
      render: (_, record) => (
        <Space size={4}>
          <Tooltip title="Ver detalle">
            <Button type="primary" ghost size="small" icon={<EyeOutlined />} onClick={() => openDetail(record)} />
          </Tooltip>
          <Tooltip title="Imprimir">
            <Button size="small" icon={<PrinterOutlined />} onClick={() => handlePrint(record)} />
          </Tooltip>
          <Tooltip title="Descargar Excel">
            <Button size="small" icon={<FileExcelOutlined />} onClick={() => handleExportFacturaExcel(record)} />
          </Tooltip>
          {canCrear && record.status === 'creada' && (
            <Tooltip title="Editar">
              <Button size="small" icon={<EditOutlined />} onClick={() => openEditModal(record)} />
            </Tooltip>
          )}
          {canDelete && record.status === 'creada' && (
            <Popconfirm
              title="¿Eliminar factura?"
              description={`La factura "${record.numero_factura}" será eliminada permanentemente.`}
              onConfirm={() => handleDelete(record)}
              okText="Sí, eliminar"
              cancelText="Cancelar"
              icon={<ExclamationCircleOutlined style={{ color: '#ff4d4f' }} />}
              okButtonProps={{ danger: true }}
            >
              <Tooltip title="Eliminar">
                <Button danger size="small" icon={<DeleteOutlined />} />
              </Tooltip>
            </Popconfirm>
          )}
        </Space>
      )
    }
  ];

  return (
    <div>
      {/* Hidden print component */}
      <div style={{ display: 'none' }}>
        <FacturaPrint ref={printRef} factura={printData} />
      </div>

      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <div>
          <Title level={3} style={{ margin: 0 }}>Facturas</Title>
          <Text type="secondary">Validación de facturas de consumo interno</Text>
        </div>
      </div>

      {/* Filters & Actions */}
      <Card bordered={false} style={{ marginBottom: 16 }}>
        <Row gutter={[12, 12]} align="middle">
          <Col xs={24} sm={10} md={8}>
            <Input
              placeholder="Buscar por N° de factura o proveedor..."
              prefix={<SearchOutlined />}
              allowClear
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                if (!e.target.value) fetchFacturas({ search: '' });
              }}
              onPressEnter={(e) => handleSearch(e.target.value)}
              onClear={() => handleSearch('')}
            />
          </Col>
          <Col xs={24} sm={8} md={6}>
            <Select
              placeholder="Filtrar por estado"
              allowClear
              style={{ width: '100%' }}
              value={statusFilter || undefined}
              onChange={handleStatusFilter}
            >
              {Object.entries(STATUS_CONFIG).map(([value, cfg]) => (
                <Option key={value} value={value}>{cfg.label}</Option>
              ))}
            </Select>
          </Col>
          <Col xs={24} sm={12} md={7}>
            <DatePicker.RangePicker
              style={{ width: '100%' }}
              format="DD/MM/YYYY"
              placeholder={['Fecha desde', 'Fecha hasta']}
              value={fechaRange}
              onChange={handleFechaRangeFilter}
            />
          </Col>
          <Col>
            <Button
              icon={<ReloadOutlined />}
              onClick={() => { setSearch(''); setStatusFilter(''); setFechaRange(null); fetchFacturas({ search: '', status: '', fechaRange: null }); }}
            >
              Limpiar
            </Button>
          </Col>
          <Col>
            <Button icon={<FileExcelOutlined />} onClick={handleExportExcel}>
              Descargar Excel
            </Button>
          </Col>
          {canCrear && (
            <Col flex="auto" style={{ textAlign: 'right' }}>
              <Button type="primary" icon={<PlusOutlined />} onClick={openCreateModal}>
                Nueva Factura
              </Button>
            </Col>
          )}
        </Row>
      </Card>

      {/* Table */}
      <Card bordered={false}>
        <Table
          dataSource={facturas}
          columns={columns}
          rowKey="id"
          loading={loading}
          size="small"
          scroll={{ x: 1160 }}
          pagination={{
            pageSize: 15,
            showSizeChanger: true,
            pageSizeOptions: ['10', '15', '25', '50'],
            showTotal: (total, range) => `${range[0]}-${range[1]} de ${total} facturas`,
            locale: { items_per_page: '/ página' }
          }}
          locale={{ emptyText: 'No se encontraron facturas' }}
        />
      </Card>

      {/* ── Modal: Nueva / Editar Factura ─────────────────────────────────── */}
      <Modal
        title={editingFactura ? `Editar Factura: ${editingFactura.numero_factura}` : 'Nueva Factura'}
        open={modalOpen}
        onOk={handleSaveFactura}
        onCancel={() => setModalOpen(false)}
        okText={editingFactura ? 'Guardar Cambios' : 'Crear Factura'}
        cancelText="Cancelar"
        confirmLoading={saving}
        width={900}
        destroyOnClose
      >
        <Form form={form} layout="vertical" requiredMark="optional" style={{ marginTop: 8 }}>
          <Row gutter={16}>
            <Col xs={24} sm={8}>
              <Form.Item
                name="numero_factura"
                label="N° de Factura"
                rules={[{ required: true, message: 'El número de factura es requerido' }, { max: 50 }]}
              >
                <Input placeholder="Ej: A-00123" maxLength={50} />
              </Form.Item>
            </Col>
            <Col xs={24} sm={10}>
              <Form.Item
                name="proveedor_nombre"
                label="Proveedor / Emisor"
                rules={[{ required: true, message: 'El proveedor es requerido' }, { max: 200 }]}
              >
                <Input placeholder="Nombre de quien emite la factura" maxLength={200} />
              </Form.Item>
            </Col>
            <Col xs={24} sm={6}>
              <Form.Item name="rfc_proveedor" label="RFC" rules={[{ max: 20 }]}>
                <Input placeholder="Opcional" maxLength={20} style={{ textTransform: 'uppercase' }} />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col xs={24} sm={8}>
              <Form.Item
                name="fecha_factura"
                label="Fecha de Factura"
                rules={[{ required: true, message: 'La fecha es requerida' }]}
              >
                <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={16}>
              <Form.Item name="concepto" label="Concepto">
                <Input placeholder="Motivo del consumo interno (opcional)" maxLength={255} />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item name="notas" label="Notas">
            <TextArea placeholder="Notas adicionales (opcional)" rows={1} maxLength={500} />
          </Form.Item>

          <Divider orientation="left" plain>Productos</Divider>

          <Table
            dataSource={lineItems}
            rowKey="key"
            pagination={false}
            size="small"
            footer={() => (
              <Button type="dashed" icon={<PlusOutlined />} onClick={addLineItem} block>
                Agregar Producto
              </Button>
            )}
            locale={{ emptyText: <Empty description="Sin productos agregados" /> }}
            columns={[
              {
                title: 'Descripción',
                key: 'descripcion',
                render: (_, li) => (
                  <Input
                    placeholder="Descripción del producto/servicio"
                    value={li.descripcion}
                    onChange={(e) => updateLineItem(li.key, 'descripcion', e.target.value)}
                  />
                )
              },
              {
                title: 'Cantidad',
                key: 'cantidad',
                width: 100,
                render: (_, li) => (
                  <InputNumber
                    min={0.001}
                    precision={3}
                    style={{ width: '100%' }}
                    value={li.cantidad}
                    onChange={(v) => updateLineItem(li.key, 'cantidad', v || 0)}
                  />
                )
              },
              {
                title: 'Costo Unit.',
                key: 'costo_unitario',
                width: 120,
                render: (_, li) => (
                  <InputNumber
                    min={0}
                    precision={2}
                    prefix="$"
                    style={{ width: '100%' }}
                    value={li.costo_unitario}
                    onChange={(v) => updateLineItem(li.key, 'costo_unitario', v || 0)}
                  />
                )
              },
              {
                title: 'IVA %',
                key: 'iva_porcentaje',
                width: 90,
                render: (_, li) => (
                  <InputNumber
                    min={0}
                    max={100}
                    precision={2}
                    style={{ width: '100%' }}
                    value={li.iva_porcentaje}
                    onChange={(v) => updateLineItem(li.key, 'iva_porcentaje', v ?? 0)}
                  />
                )
              },
              {
                title: 'Total',
                key: 'total',
                width: 110,
                align: 'right',
                render: (_, li) => <Text strong>{formatCurrency(calcularLinea(li).total)}</Text>
              },
              {
                title: '',
                key: 'acciones',
                width: 40,
                render: (_, li) => (
                  <Button danger type="text" size="small" icon={<DeleteOutlined />} onClick={() => removeLineItem(li.key)} />
                )
              }
            ]}
          />

          {lineItems.length > 0 && (
            <Row justify="end" style={{ marginTop: 12 }}>
              <Col>
                <Descriptions column={1} size="small" bordered style={{ width: 260 }}>
                  <Descriptions.Item label="Subtotal">{formatCurrency(totalesForm.subtotal)}</Descriptions.Item>
                  <Descriptions.Item label="IVA">{formatCurrency(totalesForm.iva)}</Descriptions.Item>
                  <Descriptions.Item label={<Text strong>Total</Text>}>
                    <Text strong>{formatCurrency(totalesForm.total)}</Text>
                  </Descriptions.Item>
                </Descriptions>
              </Col>
            </Row>
          )}
        </Form>
      </Modal>

      {/* ── Modal: Detalle / Revisión / Confirmación ─────────────────────── */}
      <Modal
        title={detailFactura ? `Factura: ${detailFactura.numero_factura}` : 'Factura'}
        open={detailOpen}
        onCancel={() => { setDetailOpen(false); setDetailFactura(null); setRevisionMode(false); setConfirmMode(false); }}
        footer={
          <Space wrap style={{ justifyContent: 'flex-end' }}>
            <Button onClick={() => { setDetailOpen(false); setDetailFactura(null); setRevisionMode(false); setConfirmMode(false); }}>
              Cerrar
            </Button>
            {detailFactura && !revisionMode && !confirmMode && (
              <Button icon={<PrinterOutlined />} onClick={() => handlePrint(detailFactura)}>
                Imprimir
              </Button>
            )}
            {detailFactura && !revisionMode && !confirmMode && (
              <Button icon={<FileExcelOutlined />} onClick={() => handleExportFacturaExcel(detailFactura)}>
                Descargar Excel
              </Button>
            )}
            {detailFactura && canRevisar && detailFactura.status === 'creada' && !revisionMode && (
              <Button type="primary" icon={<FileSearchOutlined />} onClick={startRevision}>
                Revisar
              </Button>
            )}
            {revisionMode && (
              <>
                <Button onClick={() => setRevisionMode(false)}>Cancelar Revisión</Button>
                <Button type="primary" icon={<CheckOutlined />} loading={actionLoading} onClick={handleGuardarRevision}>
                  Guardar Revisión
                </Button>
              </>
            )}
            {detailFactura && canConfirmar && detailFactura.status === 'revisada' && !revisionMode && !confirmMode && (
              lineasConCorreccion.length > 0 ? (
                <Button type="primary" icon={<SafetyCertificateOutlined />} onClick={startConfirm}>
                  Confirmar
                </Button>
              ) : (
                <Popconfirm
                  title="¿Confirmar factura?"
                  description="No hubo correcciones; se confirmarán los valores originales."
                  onConfirm={handleConfirmarDirecto}
                  okText="Sí, confirmar"
                  cancelText="Cancelar"
                >
                  <Button type="primary" icon={<SafetyCertificateOutlined />} loading={actionLoading}>
                    Confirmar
                  </Button>
                </Popconfirm>
              )
            )}
            {confirmMode && (
              <>
                <Button onClick={() => setConfirmMode(false)}>Cancelar</Button>
                <Button
                  type="primary"
                  icon={<SafetyCertificateOutlined />}
                  loading={actionLoading}
                  onClick={handleConfirmarConDecisiones}
                >
                  Confirmar Factura
                </Button>
              </>
            )}
          </Space>
        }
        width={900}
        destroyOnClose
      >
        {detailLoading || !detailFactura ? (
          <div style={{ textAlign: 'center', padding: 40 }}>Cargando...</div>
        ) : (
          <div>
            <Descriptions column={2} size="small" bordered style={{ marginBottom: 16 }}>
              <Descriptions.Item label="Estado" span={2}>
                <Tag color={STATUS_CONFIG[detailFactura.status]?.color}>
                  {STATUS_CONFIG[detailFactura.status]?.label || detailFactura.status}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Proveedor">{detailFactura.proveedor_nombre}</Descriptions.Item>
              <Descriptions.Item label="RFC">{detailFactura.rfc_proveedor || '—'}</Descriptions.Item>
              <Descriptions.Item label="Fecha de Factura">
                {dayjs(detailFactura.fecha_factura).format('DD/MM/YYYY')}
              </Descriptions.Item>
              <Descriptions.Item label="Concepto">{detailFactura.concepto || '—'}</Descriptions.Item>
              <Descriptions.Item label="Creada por">{detailFactura.creador?.nombre || '—'}</Descriptions.Item>
              {detailFactura.revisor?.nombre && (
                <Descriptions.Item label="Revisada por">
                  {detailFactura.revisor.nombre}
                  {detailFactura.fecha_revision && ` — ${dayjs(detailFactura.fecha_revision).format('DD/MM/YYYY HH:mm')}`}
                </Descriptions.Item>
              )}
              {detailFactura.confirmador?.nombre && (
                <Descriptions.Item label="Confirmada por">
                  {detailFactura.confirmador.nombre}
                  {detailFactura.fecha_confirmacion && ` — ${dayjs(detailFactura.fecha_confirmacion).format('DD/MM/YYYY HH:mm')}`}
                </Descriptions.Item>
              )}
              {detailFactura.notas && <Descriptions.Item label="Notas" span={2}>{detailFactura.notas}</Descriptions.Item>}
            </Descriptions>

            {revisionMode && (
              <Alert
                type="info"
                showIcon
                style={{ marginBottom: 12 }}
                message="Modo revisión"
                description="Ajusta los valores que estén incorrectos. Los datos originales se conservan y quedarán visibles junto a tu corrección."
              />
            )}

            {confirmMode && (
              <>
                <Alert
                  type="warning"
                  showIcon
                  style={{ marginBottom: 12 }}
                  message="Decide qué valor confirmar"
                  description="Esta factura tiene productos con correcciones. Para cada uno, elige si se confirma el valor corregido o se regresa al valor original ingresado en la creación."
                />
                <Table
                  dataSource={lineasConCorreccion}
                  rowKey="id"
                  pagination={false}
                  size="small"
                  style={{ marginBottom: 16 }}
                  columns={[
                    {
                      title: 'Producto',
                      key: 'descripcion',
                      render: (_, d) => (
                        <div>
                          <Text type="secondary" style={{ fontSize: 12, textDecoration: 'line-through' }}>
                            {d.descripcion_original} — {parseFloat(d.cantidad_original)} × {formatCurrency(d.costo_unitario_original)} (IVA {parseFloat(d.iva_porcentaje_original)}%)
                          </Text>
                          <br />
                          <Text strong style={{ color: '#1677ff' }}>
                            {d.descripcion_corregida ?? d.descripcion_original} — {parseFloat(d.cantidad_corregida ?? d.cantidad_original)} × {formatCurrency(d.costo_unitario_corregido ?? d.costo_unitario_original)} (IVA {parseFloat(d.iva_porcentaje_corregido ?? d.iva_porcentaje_original)}%)
                          </Text>
                        </div>
                      )
                    },
                    {
                      title: 'Decisión',
                      key: 'decision',
                      width: 260,
                      render: (_, d) => (
                        <Radio.Group
                          value={confirmDecisions[d.id] || 'corregido'}
                          onChange={(e) => setConfirmDecisions((prev) => ({ ...prev, [d.id]: e.target.value }))}
                        >
                          <Radio.Button value="corregido">Usar corrección</Radio.Button>
                          <Radio.Button value="original">Usar original</Radio.Button>
                        </Radio.Group>
                      )
                    }
                  ]}
                />
              </>
            )}

            <Divider orientation="left" plain style={{ marginTop: 0 }}>Productos</Divider>

            <Table
              dataSource={detailFactura.detalles || []}
              rowKey="id"
              pagination={false}
              size="small"
              columns={
                revisionMode
                  ? [
                      {
                        title: 'Descripción',
                        key: 'descripcion',
                        render: (_, d) => (
                          <Input
                            value={revisionValues[d.id]?.descripcion}
                            onChange={(e) => updateRevisionValue(d.id, 'descripcion', e.target.value)}
                          />
                        )
                      },
                      {
                        title: 'Cantidad',
                        key: 'cantidad',
                        width: 100,
                        render: (_, d) => (
                          <InputNumber
                            min={0.001}
                            precision={3}
                            style={{ width: '100%' }}
                            value={revisionValues[d.id]?.cantidad}
                            onChange={(v) => updateRevisionValue(d.id, 'cantidad', v || 0)}
                          />
                        )
                      },
                      {
                        title: 'Costo Unit.',
                        key: 'costo_unitario',
                        width: 120,
                        render: (_, d) => (
                          <InputNumber
                            min={0}
                            precision={2}
                            prefix="$"
                            style={{ width: '100%' }}
                            value={revisionValues[d.id]?.costo_unitario}
                            onChange={(v) => updateRevisionValue(d.id, 'costo_unitario', v || 0)}
                          />
                        )
                      },
                      {
                        title: 'IVA %',
                        key: 'iva_porcentaje',
                        width: 90,
                        render: (_, d) => (
                          <InputNumber
                            min={0}
                            max={100}
                            precision={2}
                            style={{ width: '100%' }}
                            value={revisionValues[d.id]?.iva_porcentaje}
                            onChange={(v) => updateRevisionValue(d.id, 'iva_porcentaje', v ?? 0)}
                          />
                        )
                      },
                      {
                        title: 'Original',
                        key: 'original',
                        width: 130,
                        render: (_, d) => (
                          <Text type="secondary" style={{ fontSize: 12 }}>
                            {parseFloat(d.cantidad_original)} × {formatCurrency(d.costo_unitario_original)}
                            <br />IVA {parseFloat(d.iva_porcentaje_original)}%
                          </Text>
                        )
                      }
                    ]
                  : [
                      {
                        title: 'Descripción',
                        key: 'descripcion',
                        render: (_, d) =>
                          d.fue_corregido ? (
                            <div>
                              <Text delete type="secondary" style={{ fontSize: 12 }}>{d.descripcion_original}</Text>
                              <br />
                              <Text strong style={{ color: '#1677ff' }}>{d.descripcion_efectiva}</Text>
                            </div>
                          ) : (
                            <Text>{d.descripcion_original}</Text>
                          )
                      },
                      {
                        title: 'Cantidad',
                        key: 'cantidad',
                        width: 110,
                        align: 'right',
                        render: (_, d) =>
                          d.fue_corregido && parseFloat(d.cantidad_corregida ?? d.cantidad_original) !== parseFloat(d.cantidad_original) ? (
                            <div>
                              <Text delete type="secondary" style={{ fontSize: 12 }}>{parseFloat(d.cantidad_original)}</Text>
                              <br />
                              <Text strong style={{ color: '#1677ff' }}>{d.cantidad_efectiva}</Text>
                            </div>
                          ) : (
                            <Text>{parseFloat(d.cantidad_original)}</Text>
                          )
                      },
                      {
                        title: 'Costo Unit.',
                        key: 'costo_unitario',
                        width: 120,
                        align: 'right',
                        render: (_, d) =>
                          d.fue_corregido && parseFloat(d.costo_unitario_corregido ?? d.costo_unitario_original) !== parseFloat(d.costo_unitario_original) ? (
                            <div>
                              <Text delete type="secondary" style={{ fontSize: 12 }}>{formatCurrency(d.costo_unitario_original)}</Text>
                              <br />
                              <Text strong style={{ color: '#1677ff' }}>{formatCurrency(d.costo_unitario_efectivo)}</Text>
                            </div>
                          ) : (
                            <Text>{formatCurrency(d.costo_unitario_original)}</Text>
                          )
                      },
                      {
                        title: 'IVA %',
                        key: 'iva_porcentaje',
                        width: 90,
                        align: 'right',
                        render: (_, d) =>
                          d.fue_corregido && parseFloat(d.iva_porcentaje_corregido ?? d.iva_porcentaje_original) !== parseFloat(d.iva_porcentaje_original) ? (
                            <div>
                              <Text delete type="secondary" style={{ fontSize: 12 }}>{parseFloat(d.iva_porcentaje_original)}%</Text>
                              <br />
                              <Text strong style={{ color: '#1677ff' }}>{d.iva_porcentaje_efectivo}%</Text>
                            </div>
                          ) : (
                            <Text>{parseFloat(d.iva_porcentaje_original)}%</Text>
                          )
                      },
                      {
                        title: 'Total',
                        key: 'total',
                        width: 110,
                        align: 'right',
                        render: (_, d) => <Text strong>{formatCurrency(d.total_efectivo)}</Text>
                      }
                    ]
              }
            />

            {!revisionMode && !confirmMode && (
              <Row justify="end" style={{ marginTop: 12 }}>
                <Col>
                  <Descriptions column={1} size="small" bordered style={{ width: 260 }}>
                    <Descriptions.Item label="Subtotal">{formatCurrency(detailFactura.subtotal)}</Descriptions.Item>
                    <Descriptions.Item label="IVA">{formatCurrency(detailFactura.iva_total)}</Descriptions.Item>
                    <Descriptions.Item label={<Text strong>Total</Text>}>
                      <Text strong>{formatCurrency(detailFactura.total)}</Text>
                    </Descriptions.Item>
                  </Descriptions>
                </Col>
              </Row>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
}

export default Facturas;
